
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import type { GetStaticPropsResult } from 'next';
import type { ProductWithPrice } from 'types';
import clsxm from '@/lib/clsxm';
import { getActiveProductsWithPrices, getTeamProductsWithPrices, } from '@/data/products';
import AffiliatesAudience from '@/components/affiliates/AffiliatesAudience';
import AffiliatesCalculator from '@/components/affiliates/AffiliatesCalculator';
import AffiliatesFaq from '@/components/affiliates/AffiliatesFaq';
import AffiliatesFinalCta from '@/components/affiliates/AffiliatesFinalCta';
import AffiliatesHero from '@/components/affiliates/AffiliatesHero';
import AffiliatesRules from '@/components/affiliates/AffiliatesRules';
import AffiliatesSteps from '@/components/affiliates/AffiliatesSteps';
import type { CalculatorPlan } from '@/components/affiliates/kit';
import { tone } from '@/components/agents/primitives';
import TeamsLogos from '@/components/b2b/TeamsLogos';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';
type Props = {
    plans: CalculatorPlan[];
};
/*
 * The affiliate program (English only: the localized URLs redirect here).
 * Sign-up, the dashboard and the terms live on Rewardful.
 */
export default function Affiliates({ plans }: Props) {
    const example = plans.find((plan) => plan.name === 'Unlimited') ?? plans[plans.length - 1];
    return (<main className={clsxm('relative overflow-x-clip', tone.page)}>
      <Seo templateTitle='Affiliate Program: Earn 30% Recurring Commission' description='Join the Easy-Peasy.AI affiliate program for free and earn 30% of every payment from the customers you refer, paid monthly to your PayPal.' image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent('Affiliate Program')}`} structuredData={false}/>
      <AffiliatesHero example={example}/>
      <TeamsLogos />
      <AffiliatesSteps />
      <AffiliatesCalculator plans={plans}/>
      <div className='pt-24 sm:pt-32'>
        <AffiliatesAudience />
      </div>
      <AffiliatesRules />
      <AffiliatesFaq />
      <AffiliatesFinalCta />
      <Footer />
    </main>);
}
/** Lowest active monthly price of a product, in dollars. */
function monthlyPrice(product?: ProductWithPrice) {
    const cents = (product?.prices ?? [])
        .filter((price) => price.active && price.interval === 'month')
        .map((price) => price.unit_amount ?? 0)
        .filter((amount) => amount > 0);
    return cents.length ? Math.min(...cents) / 100 : null;
}
// Used only if a plan can't be read, so the calculator still works.
const FALLBACK: Record<string, number> = {
    Starter: 16,
    'Unlimited 50': 24,
    Unlimited: 32,
    Teams: 68,
};
async function getStaticProps(): Promise<GetStaticPropsResult<Props>> {
    const [plans, teams] = await Promise.all([
        getActiveProductsWithPrices().catch(() => [] as ProductWithPrice[]),
        getTeamProductsWithPrices().catch(() => [] as ProductWithPrice[]),
    ]);
    const byName = (name: string) => monthlyPrice((plans as ProductWithPrice[]).find((product) => product.name === name)) ?? FALLBACK[name];
    const teamPrices = (teams as ProductWithPrice[])
        .map((product) => monthlyPrice(product))
        .filter((price): price is number => price !== null);
    return {
        props: {
            plans: [
                { name: 'Starter', monthly: byName('Starter'), start: 10, max: 100 },
                {
                    name: 'Unlimited 50',
                    monthly: byName('Unlimited 50'),
                    start: 20,
                    max: 200,
                },
                {
                    name: 'Unlimited',
                    monthly: byName('Unlimited'),
                    start: 10,
                    max: 200,
                },
                {
                    name: 'Teams',
                    monthly: teamPrices.length ? Math.min(...teamPrices) : FALLBACK.Teams,
                    from: true,
                    start: 3,
                    max: 50,
                },
            ],
        },
        revalidate: 3600,
    };
}

    async function __Next_Translate__getStaticProps__1a167d9f3d7__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/affiliates',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a167d9f3d7__ as getStaticProps }
  